import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { Chip, Empty } from "@/components/ui-bits";

const Trend = ({ t }) => {
  if (!t || t === "→") return <span className="ss">→</span>;
  const [arrow, kind] = t.split(" ");
  return <span style={{ color: kind === "good" ? "var(--ok,#1a7f37)" : "var(--bad,#b42318)", fontWeight: 700 }} title={kind}>{arrow}</span>;
};

export const MonthlyReport = () => {
  const [months, setMonths] = useState(null);
  const [month, setMonth] = useState(null);
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadList = useCallback(() => api.get("/admin/monthly-reports").then((r) => { setMonths(r.data); setMonth((m) => m || r.data[0]?.month || null); }), []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (month) api.get(`/admin/monthly-reports/${month}`).then((r) => setRep(r.data)); }, [month]);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/automations/monthlyReport/run"); toast.success(`Report card for ${r.data.result.month} generated and emailed`); setMonth(r.data.result.month); loadList(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!months) return <div className="center"><span className="spin" /></div>;
  return (
    <div className="panel fade" data-testid="monthly-report">
      <div className="ph"><h3>Monthly report card · emailed on the 1st at 06:00</h3>
        <div className="actions">
          {months.length > 0 && <select value={month || ""} onChange={(e) => setMonth(e.target.value)} data-testid="monthly-month-select">{months.map((m) => <option key={m.month} value={m.month}>{m.label}</option>)}</select>}
          <button className="btn sm" disabled={busy} onClick={run} data-testid="run-monthly-report-button">{busy ? "Running…" : "Run now (last month)"}</button>
        </div></div>
      {months.length === 0 && <Empty>No report cards yet — tap "Run now" to generate last month's.</Empty>}
      {rep && (
        <>
          <div className="ss" style={{ marginBottom: 8 }} data-testid="monthly-report-meta">{rep.label} · {rep.rows.length} clients · generated {fmt(rep.generatedAt, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · arrows compare with the month before</div>
          <table className="tbl" data-testid="monthly-report-table"><thead><tr><th>Client</th><th>Sessions</th><th>Adherence</th><th>Full weeks</th><th>Mood</th><th>Sleep</th><th>Soreness</th><th>Deloads</th><th>Flags</th></tr></thead>
            <tbody>{rep.rows.map((r) => (
              <tr key={r.clientId} data-testid={`monthly-row-${r.clientId}`}>
                <td><div style={{ display: "flex", alignItems: "center", gap: 8 }}><Avatar client={{ id: r.clientId, photoKey: r.photoKey, name: r.name }} size={26} radius={7} /><b>{r.name}</b></div></td>
                <td className="num">{r.completed}/{r.scheduled}{r.noShows ? <span className="ss"> · {r.noShows} no-show</span> : null}</td>
                <td className="num">{r.adherence ?? "—"}{r.adherence != null && "%"} <Trend t={r.trends.adherence} /></td>
                <td className="num">{r.fullWeeks} {r.prev.fullWeeks != null && <span className="ss">(was {r.prev.fullWeeks})</span>}</td>
                <td className="num">{r.mood ?? "—"} <Trend t={r.trends.mood} /></td>
                <td className="num">{r.sleep ?? "—"} <Trend t={r.trends.sleep} /></td>
                <td className="num">{r.soreness ?? "—"} <Trend t={r.trends.soreness} /></td>
                <td className="num">{r.deloads || "—"}</td>
                <td><div className="hl">{r.flags.length === 0 ? <Chip cls="ok" label="on track" /> : r.flags.map((f) => <Chip key={f} cls="bad" label={`⚠ ${f}`} />)}</div></td>
              </tr>))}</tbody></table>
        </>
      )}
    </div>
  );
};
